import React, { Suspense, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './css/tokens.css'; // Design tokens — must be first
import './index.css';
import './css/style.css'; // Global styles
import reportWebVitals from './reportWebVitals';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';

// JokePage is imported EAGERLY, unlike every other route below. Deliberate:
// /joke is reached by scanning a QR code on a singlet, so it is always a cold
// direct load on park LTE. A lazy chunk would cost a second round trip and show
// the "Loading..." fallback as the opening beat of a comedy page. It is ~2KB
// against a ~190KB main.js. Do not "fix" this to match the pattern below.
import JokePage from './components/joke/JokePage';
// ChicagoPage is eager for the same reason as JokePage: it is the Instagram-bio
// destination, always a cold direct load in the in-app browser, where a lazy
// chunk would open the campaign page on the "Loading..." fallback. ~2KB.
import ChicagoPage from './components/chicago/ChicagoPage';

// Route-level code splitting (Phase 6)
const App = React.lazy(() => import('./App'));
const LandingPage = React.lazy(() => import('./components/LandingPage'));
const WorkoutViewer = React.lazy(() => import('./components/WorkoutViewer'));
const WorkoutSession = React.lazy(() => import('./components/WorkoutSession'));
const Onboarding = React.lazy(() => import('./components/Onboarding'));
const OnboardingSuccess = React.lazy(() => import('./components/OnboardingSuccess'));
const ProgressDashboard = React.lazy(() => import('./components/progress/ProgressDashboard'));
const PrivacyPolicy = React.lazy(() =>
  import('./components/LegalPages').then(m => ({ default: m.PrivacyPolicy }))
);
const TermsOfService = React.lazy(() =>
  import('./components/LegalPages').then(m => ({ default: m.TermsOfService }))
);
const AchillesSharedView = React.lazy(() => import('./components/AchillesSharedView'));
const AdminConversations = React.lazy(() => import('./components/AdminConversations'));
const PublicRacePage = React.lazy(() => import('./components/race/PublicRacePage'));

// GA4 page_view tracking on route changes (production only)
function GA4RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    if (window.gtag) {
      // SMS links carry the client token (?token=); it must never reach analytics.
      let href = window.location.href;
      try {
        const u = new URL(href);
        u.searchParams.delete('token');
        href = u.toString();
      } catch (e) { /* keep href */ }
      window.gtag('event', 'page_view', {
        page_path: location.pathname,
        page_location: href,
        page_title: document.title,
      });
    }
  }, [location]);
  return null;
}

const root = ReactDOM.createRoot(document.getElementById('root'));

// Get Google Client ID from environment variable, with fallback for development
const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  (process.env.REACT_APP_DISABLE_GOOGLE_OAUTH === 'true'
    ? '' // Empty string if OAuth is disabled
    : '710204795898-fah8s6r5vu441p2ma37gjpv42qd75h5g.apps.googleusercontent.com'); // Fallback for existing setup

root.render(
  <React.StrictMode>
    <GoogleOAuthProvider clientId={googleClientId}>
      <Router>
        <GA4RouteTracker />
        <Suspense fallback={
          <div aria-live="polite" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <p>Loading...</p>
          </div>
        }>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/joke" element={<JokePage />} />
            <Route path="/chicago" element={<ChicagoPage />} />
            {/* The free race plan, no account: /race/nyc?goal=3:30 */}
            <Route path="/race/:slug" element={<PublicRacePage />} />
            <Route path="/trainer" element={<App />} />
            {/* 💳 Billing checkout return — App detects the /billing/* pathname and
                polls for access, then redirects to the plan. */}
            <Route path="/billing/success" element={<App />} />
            <Route path="/billing/cancel" element={<App />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/onboarding/success" element={<OnboardingSuccess />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/workout/:workoutId" element={<WorkoutViewer />} />
            <Route path="/workout-session/:workoutId" element={<WorkoutSession />} />
            <Route path="/progress/:clientId" element={<ProgressDashboard />} />
            <Route path="/achilles/view/:token" element={<AchillesSharedView />} />
            <Route path="/admin/conversations" element={<AdminConversations />} />
          </Routes>
        </Suspense>
      </Router>
    </GoogleOAuthProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
